import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { ReportService, Report, ReportsListResponse } from '../services/report.service';
import { StandortService } from '../services/standort.service';
import { PaginationService } from '../core/services/pagination.service';

export interface FilterOptions {
  client_id?: string;
  standort_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
}

@Component({
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['einsatznummer', 'client_name', 'standort_name', 'techniker_name', 'created_at', 'status', 'actions'];
  dataSource: MatTableDataSource<Report>;
  
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private destroy$ = new Subject<void>();
  
  loading = false;
  totalItems = 0;
  currentPage = 1;
  pageIndex = 0; // 0-based page index for mat-paginator
  pageSize: number;
  pageSizeOptions: number[];
  searchTerm = '';
  
  // Filter options
  filters: FilterOptions = {};
  standorte: any[] = [];
  
  // Status dropdown state
  statusDropdownOpen = false;
  
  
  // Status options
  statusOptions = [
    { value: '', label: 'Alle Status' },
    { value: 'generated', label: 'Generiert' },
    { value: 'sent', label: 'Versendet' }
  ];

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    private reportService: ReportService,
    private standortService: StandortService,
    private paginationService: PaginationService
  ) {
    this.dataSource = new MatTableDataSource<Report>([]);
    // Initialize pagination from service
    this.pageSize = this.paginationService.getPageSize();
    this.pageSizeOptions = this.paginationService.getPageSizeOptions();
  }

  ngOnInit(): void {
    console.log('✅ ReportsComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    
    // Set default filter to only show "versendet" (sent) reports
    this.filters.status = 'sent';
    
    this.loadFilterOptions();
    this.loadReports();
    
    // Add click outside listener to close dropdown
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Remove click outside listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  onDocumentClick(event: Event): void {
    // Close status dropdown when clicking outside
    this.statusDropdownOpen = false;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadFilterOptions(): void {
    // SECURITY: Load standorte - API filters server-side by kontakt permissions
    // The API endpoint /v5/standort automatically filters to only return standorte
    // that are in the kontakt_standort_berechtigungen table for this kontakt user.
    // The frontend MUST NOT filter this list - it only displays what the API returns.
    // Use high limit to get all permitted standorte (API will only return permitted ones)
    this.standortService.getStandorte(1, 10000).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // IMPORTANT: Use API response directly - do NOT filter client-side
        // The API has already filtered to only permitted standorte
        this.standorte = response.data || [];
        console.log('Loaded standorte for filter (server-filtered by permissions):', this.standorte.length);
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
        this.snackBar.open('Fehler beim Laden der Standorte', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadReports(): void {
    this.loading = true;
    this.pageIndex = this.currentPage - 1; // Convert 1-based to 0-based for mat-paginator
    
    const filters = {
      ...this.filters,
      search: this.searchTerm || undefined,
      page: this.currentPage,
      limit: this.pageSize
    };
    
    this.reportService.getReports(filters).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: ReportsListResponse) => {
        // Filter to only show latest version per kontrolltermin_id
        // Group by kontrolltermin_id and keep only the one with highest version
        const reportsByKontrolltermin = new Map<string, Report>();
        
        response.reports.forEach(report => {
          const key = report.kontrolltermin_id;
          const existing = reportsByKontrolltermin.get(key);
          
          if (!existing || (report.version || 0) > (existing.version || 0)) {
            reportsByKontrolltermin.set(key, report);
          }
        });
        
        // Convert map back to array and sort by created_at desc
        const filteredReports = Array.from(reportsByKontrolltermin.values())
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        
        this.dataSource.data = filteredReports;
        this.totalItems = filteredReports.length;
        this.loading = false;
        
        // Update paginator page index if it exists
        if (this.paginator) {
          this.paginator.pageIndex = this.pageIndex;
        }
        
        console.log('✅ Reports loaded (filtered to latest per kontrolltermin):', filteredReports.length);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Fehler beim Laden der Berichte', 'Schließen', { duration: 3000 });
        console.error('Error loading reports:', error);
      }
    });
  }

  onSearch(): void {
    // For now, just reload with current filters
    this.currentPage = 1;
    this.pageIndex = 0;
    this.loadReports();
  }

  /**
   * Get status label for display
   */
  getStatusLabel(): string {
    if (!this.filters.status || this.filters.status === '') {
      return 'Alle Status';
    } else if (this.filters.status === 'generated') {
      return 'Generiert';
    } else if (this.filters.status === 'sent') {
      return 'Versendet';
    }
    return 'Alle Status';
  }

  /**
   * Toggle status dropdown
   */
  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  /**
   * Handle status change
   */
  onStatusChange(statusValue: string): void {
    this.filters.status = statusValue === '' ? undefined : statusValue;
    this.statusDropdownOpen = false;
    this.onFilterChange();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.pageIndex = 0;
    this.loadReports();
  }

  clearFilters(): void {
    this.filters = {};
    this.searchTerm = '';
    
    this.currentPage = 1;
    this.pageIndex = 0;
    this.loadReports();
  }

  onStandortChange(): void {
    // If empty string selected, clear the filter
    if (this.filters.standort_id === '') {
      this.filters.standort_id = undefined;
    }
    this.onFilterChange();
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.paginationService.setPageSize(event.pageSize);
    this.loadReports();
  }

  downloadReport(report: Report): void {
    // Generate filename: <Standort-name>-SBK1-Monitoring-<Monat>-<Jahr>.pdf
    const standortName = report.standort_name || 'Standort';
    // Sanitize standort name for filename (remove invalid characters)
    const sanitizedStandortName = standortName
      .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '') // Remove special chars except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .substring(0, 50); // Limit length
    
    // Get month and year from created_at date
    const createdDate = new Date(report.created_at);
    const monthIndex = createdDate.getMonth(); // 0-11
    const year = createdDate.getFullYear(); // Full 4-digit year (e.g., 2025)
    
    // German month names
    const germanMonths = [
      'Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    const month = germanMonths[monthIndex];
    
    // Generate filename: <Standort-name>-SBK1-Monitoring-<Year>-<Month>.pdf
    const filename = `${sanitizedStandortName}-SBK1-Monitoring-${year}-${month}.pdf`;
    
    this.reportService.downloadReportById(report.uuid, filename);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'generated':
        return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return 'Versendet';
      case 'generated':
        return 'Generiert';
      default:
        return 'Unbekannt';
    }
  }

  formatFileSize(bytes: number): string {
    return this.reportService.formatFileSize(bytes);
  }

  formatDate(dateString: string): string {
    return this.reportService.formatDate(dateString);
  }

  /**
   * Format date only (without time)
   */
  formatDateOnly(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Format time only with "Uhr" suffix
   */
  formatTimeOnly(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }) + ' Uhr';
  }


  /**
   * Get client name for display (similar to standort component)
   */
  getClientName(report: Report): string {
    // Use client_name which is always available
    return report.client_name || '-';
  }

  /**
   * Get report category label
   */
  getReportCategoryLabel(category?: string): string {
    if (!category || category === 'standard') {
      return 'Besucherbericht';
    }
    const categories = this.reportService.getAvailableReportCategories();
    const categoryOption = categories.find(c => c.value === category);
    // For monitoring app, always return "Besucherbericht" for standard reports
    if (categoryOption?.value === 'standard') {
      return 'Besucherbericht';
    }
    return categoryOption?.label || category;
  }

}
